import {useCallback,useLayoutEffect,useRef} from 'react';

const DELAYS=[1500,2500,4000,7000,10000,15000] as const;

/** One qualified GET sequence at a time. Never owns commands or operation IDs. */
export function usePrivateGamePolling({key,enabled,read}:{key:unknown;enabled:boolean;read:(signal:AbortSignal,automatic:boolean)=>Promise<boolean>}){
 const latest=useRef(read),state=useRef({alive:false,active:false,generation:0,flight:null as Promise<void>|null,controller:null as AbortController|null,timer:null as ReturnType<typeof setTimeout>|null,backoff:0});
 useLayoutEffect(()=>{latest.current=read;});
 const clear=useCallback(()=>{const s=state.current;if(s.timer!==null){clearTimeout(s.timer);s.timer=null;}s.generation++;s.controller?.abort();},[]);
 const refresh=useCallback((automatic=false):Promise<void>=>{
  const s=state.current;
  if(!s.alive||document.visibilityState==='hidden')return Promise.resolve();
  if(s.flight)return s.flight;
  if(s.timer!==null){clearTimeout(s.timer);s.timer=null;}
  const generation=s.generation,controller=new AbortController();s.controller=controller;
  const fn=latest.current;
  // Keep the flight even after abort: adapters may settle late or ignore abort.
  const flight=Promise.resolve().then(()=>fn(controller.signal,automatic)).then(changed=>{
   if(generation===s.generation&&!controller.signal.aborted)s.backoff=changed?0:Math.min(s.backoff+1,DELAYS.length-1);
  },()=>{if(generation===s.generation&&!controller.signal.aborted)s.backoff=Math.min(s.backoff+1,DELAYS.length-1);}).finally(()=>{
   if(s.flight===flight){s.flight=null;s.controller=null;}
   if(s.alive&&s.active&&document.visibilityState!=='hidden'){
    if(s.timer!==null)clearTimeout(s.timer);
    s.timer=setTimeout(()=>{s.timer=null;void refresh(true);},generation!==s.generation?0:DELAYS[s.backoff]);
   }
  });
  s.flight=flight;return flight;
 },[]);
 useLayoutEffect(()=>{
  const s=state.current;s.alive=true;s.active=enabled;s.backoff=0;clear();
  if(enabled&&document.visibilityState!=='hidden')s.timer=setTimeout(()=>{s.timer=null;void refresh(true);},0);
  const visibility=()=>{clear();s.backoff=0;if(s.active&&document.visibilityState!=='hidden')void refresh(true);};
  document.addEventListener('visibilitychange',visibility);
  return()=>{s.alive=false;s.active=false;clear();document.removeEventListener('visibilitychange',visibility);};
 },[key,enabled,clear,refresh]);
 return {refresh};
}
